import { DateHelper } from "@/Common/Helpers/DateHelper";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";

/**
 * Helper class for managing care element grid operations.
 */
export class FrequencyDateHelper {
    public static readonly frequencyOffsets: Record<
    CareElementFrequencyType,
    { years?: number; months?: number }
    > = {
            [CareElementFrequencyType.None]: {},
            [CareElementFrequencyType.ThreeMonths]: { months: 3 },
            [CareElementFrequencyType.SixMonths]: { months: 6 },
            [CareElementFrequencyType.Annual]: { years: 1 },
            [CareElementFrequencyType.Biennial]: { years: 2 }
        };

    /**
     * Determines whether a frequency warning badge should be displayed.
     * @param frequency The care element frequency type.
     * @param encounterDate The date of the encounter.
     * @param value The date value to validate.
     * @returns True if the warning badge should be displayed, otherwise false.
     */
    public static shouldShowFrequencyWarningBadge(
        frequency: CareElementFrequencyType,
        encounterDate: Date,
        value?: Date | null
    ): boolean {
        if (!encounterDate || !frequency) {
            return false;
        }

        const encounter = LuxonDateTimeParser.parse(encounterDate);
        if (!encounter) {
            return false;
        }

        const reviewedDate = LuxonDateTimeParser.parse(value ?? null);

        if (!reviewedDate) {
            return true;
        }

        if (reviewedDate > encounter) {
            return false;
        }

        const minValidDate = this.getMinValidDate(encounterDate, frequency);
        return !DateHelper.isInRangeOrSameDay(value, minValidDate, encounterDate);
    }

    /**
     * Calculates the minimum valid date based on the frequency.
     * @param encounter The encounter date.
     * @param frequency The care element frequency type.
     * @returns The minimum valid date.
     */
    public static getMinValidDate(encounter: Date, frequency: CareElementFrequencyType): Date {
        const encounterDateTime = LuxonDateTimeParser.parse(encounter);
        const offset = this.frequencyOffsets[frequency] ?? 0;

        return encounterDateTime.minus(offset).toJSDate();
    }
}
